import { ScheduleMeeting, StartTimeEventEmit, TimeSlot } from 'react-schedule-meeting';
import './appointment-calendar.scss';
import { de } from 'date-fns/locale';
import { useState, useCallback, useMemo } from 'react';
import { Schedule } from '../../core/models/schedule';
import { useTranslation } from 'react-i18next';
import { Appointment } from '../../core/models/appointment';
import dayjs from 'dayjs';
import { AppointmentCustomerModal } from './components/appointment-customer-modal/appointment-customer-modal';
import { timeSlotDifference } from '../../core/utils/date.utils';

type AppointmentCalendarProps = {
	schedule: Schedule[];
	appointmentDuration: number;
	reservedAppointments: Appointment[];
	onUpdate: () => void;
};

export const AppointmentCalendar: React.FC<AppointmentCalendarProps> = ({ schedule, appointmentDuration, reservedAppointments, onUpdate }) => {
	const { t } = useTranslation();

	const [modalOpen, setModalOpen] = useState(false);
	const [startTimeEvent, setStartTimeEvent] = useState<StartTimeEventEmit>();

	function calculateAvailableTimeSlots(): TimeSlot[] {
		const daysAhead = window.bookrPluginData.settings.bookingDaysAhead;
		let availableTimeSlots: TimeSlot[] = [];

		const parseTime = (time?: string): [number, number] => {
			if (!time) return [0, 0];
			const [hour, minute] = time.split(':').map(Number);
			return [hour || 0, minute || 0];
		};

		for (let i = 0; i <= daysAhead; i++) {
			const currentDate = dayjs().add(i, 'day');
			const currentWeekDay = dayjs(currentDate).format('dddd');

			const currentSchedule = schedule.find((s) => s.weekday === currentWeekDay && s.enabled);
			if (!currentSchedule) continue;

			const [startHour, startMinute] = parseTime(currentSchedule.startTime.toString());
			const [endHour, endMinute] = parseTime(currentSchedule.endTime.toString());

			availableTimeSlots.push({
				id: i,
				startTime: currentDate.set('hour', startHour).set('minute', startMinute).set('second', 0).toDate(),
				endTime: currentDate.set('hour', endHour).set('minute', endMinute).set('second', 0).toDate(),
			});
		}

		return availableTimeSlots;
	}

	function calculateUnavailableTimeSlots(): TimeSlot[] {
		const unavailableTimeSlots: TimeSlot[] = reservedAppointments.map((appointment) => {
			return {
				startTime: dayjs(appointment.startTime).toDate(),
				endTime: dayjs(appointment.startTime).add(appointmentDuration, 'minute').toDate(),
			} as TimeSlot;
		});

		return unavailableTimeSlots;
	}

	const timeSlots = useMemo(() => {
		if (schedule.length) {
			const availableSlots = calculateAvailableTimeSlots();
			const unavailableSlots = calculateUnavailableTimeSlots();

			return timeSlotDifference(availableSlots, unavailableSlots);
		}

		return [];
	}, [schedule, reservedAppointments]);

	const openModal = useCallback((startTimeEventEmit: StartTimeEventEmit) => {
		setModalOpen(true);
		setStartTimeEvent(startTimeEventEmit);
	}, []);

	return (
		<>
			<div className='calendar-schedule-meeting'>
				<ScheduleMeeting
					borderRadius={10}
					primaryColor={window.bookrPluginData.primaryColor}
					eventDurationInMinutes={appointmentDuration}
					availableTimeslots={timeSlots}
					onStartTimeSelect={openModal}
					startTimeListStyle='scroll-list'
					format_selectedDateDayTitleFormatString='cccc, do LLLL'
					format_startTimeFormatString='HH:mm'
					locale={de}
					lang_cancelButtonText={t('common.cancel')}
					lang_confirmButtonText={t('frontEnd.calendar.confirm')}
					lang_emptyListText={t('frontEnd.calendar.noTimesAvailable')}
					lang_goToNextAvailableDayText={t('frontEnd.calendar.nextAvailable')}
					lang_noFutureTimesText={t('frontEnd.calendar.noFutureTimesAvailable')}
					lang_selectedButtonText={t('frontEnd.calendar.selected')}
				/>
			</div>
			<AppointmentCustomerModal isOpen={modalOpen} startTimeEvent={startTimeEvent} onClose={() => setModalOpen(false)} onSubmitAppointment={onUpdate} />
		</>
	);
};
